/**
 * A battery, its five guns, its envelope, and the shell it throws — the *nouns*
 * of AD-010, with none of the tick in them.
 *
 * Split out of `flak.ts` when that file went past its size ceiling, along the
 * seam it already had. Everything here is either a record shape or a pure
 * function of one battery and one aeroplane: where a battery stands, whether it
 * may engage, how long its fuse burns, how well one of its guns has ranged in.
 * `flak.ts` keeps the thing those answers are *for* — the tick that lays the
 * guns, fires them and resolves the shells — and re-exports all of this, so no
 * consumer moved.
 *
 * The constants every one of these functions reads are one layer further down
 * again, in `flak-shell.ts`, beside their `AA.DAT` byte offsets.
 */

import { type Vec3 } from "../flight/math3d.ts";
import type { CombatBody } from "./geometry.ts";
import type { EncounterSide } from "./encounter-sides.ts";
import {
  AA_DAT_FIELDS,
  FLAK_ENVELOPE,
  FLAK_SLOTS_PER_BATTERY,
  FLAK_TIMER_UNIT_SECONDS,
  FUSE_BASE_UNITS,
  METRES_PER_UNIT,
  type FlakClass,
} from "./flak-shell.ts";

export interface FlakBatterySpec {
  readonly id: string;
  readonly side: EncounterSide;
  /** Ground position. `y` is the emplacement's altitude and is 0 for every battery the original places. */
  readonly position: Vec3;
  readonly batteryClass: FlakClass;
}

/** One of a battery's five guns: who it is shooting at, when it next fires, how well it has ranged in. */
export interface FlakSlot {
  readonly targetId: string | null;
  /** Seconds until this gun fires again. */
  readonly timerS: number;
  /** Shots taken at the current target, capped at `AA_DAT_FIELDS.trackingCap`. */
  readonly trackingShots: number;
}

export interface FlakBattery {
  readonly id: string;
  readonly side: EncounterSide;
  readonly position: Vec3;
  readonly batteryClass: FlakClass;
  readonly slots: readonly FlakSlot[];
}

/** A burst the renderer should draw. Purely an output — see `stepFlak` on why bursts are not state. */
export interface FlakBurst {
  readonly batteryId: string;
  readonly targetId: string;
  /** Where the shell went off: ahead of the target, in the target's own frame. */
  readonly position: Vec3;
  /** Seconds the burst is in the air, which is also its fuse. */
  readonly lifeS: number;
  /** True if this one damages its target. Decided by `burstLethality`, i.e. by where it went off. */
  readonly lethal: boolean;
  /**
   * How far the shell went off from the aeroplane's *track* — the scatter
   * across the flight path, with the along-track component taken out, because
   * the aeroplane flies through that one. This is what the effect radius is
   * measured against.
   */
  readonly missM: number;
  /** 0, 1 or 2 — how well the gun that fired it had ranged in. */
  readonly trackingLevel: number;
}

/** What flak needs to know about an aeroplane. Deliberately not `CombatAircraft` — this module does not depend on the roster. */
export interface FlakTarget {
  readonly id: string;
  readonly side: EncounterSide;
  readonly body: CombatBody;
  readonly speedMps: number;
  /** Destroyed aircraft are not shot at again. */
  readonly destroyed: boolean;
  /** The one aircraft COMBAT LEVEL scales (AD-010 §9.4) — every AI target takes a burst's full damage. */
  readonly isPlayer: boolean;
}

export function createFlakBattery(spec: FlakBatterySpec): FlakBattery {
  return {
    id: spec.id,
    side: spec.side,
    position: spec.position,
    batteryClass: spec.batteryClass,
    slots: Array.from({ length: FLAK_SLOTS_PER_BATTERY }, () => ({
      targetId: null,
      timerS: 0,
      trackingShots: 0,
    })),
  };
}

/** Metres of horizontal separation. The original's range test ignores altitude entirely — the band below does that job. */
export function horizontalRangeM(battery: FlakBattery, target: FlakTarget): number {
  return Math.hypot(target.body.position.x - battery.position.x, target.body.position.z - battery.position.z);
}

/**
 * The engagement test, `aa.c`'s in order: altitude ceiling, altitude floor,
 * then horizontal range against the battery's class radius.
 */
export function flakEngages(battery: FlakBattery, target: FlakTarget): boolean {
  if (target.destroyed || target.side === battery.side) return false;
  const altitudeM = target.body.position.y;
  if (altitudeM >= FLAK_ENVELOPE.ceilingM) return false;
  if (altitudeM <= FLAK_ENVELOPE.floorM) return false;
  return horizontalRangeM(battery, target) < FLAK_ENVELOPE.rangeM[battery.batteryClass];
}

/** `(battery.z >> 7) + 4` timer units, as seconds. The shell's time of flight, and its fuse. */
export function fuseSecondsOf(battery: FlakBattery): number {
  const altitudeUnits = Math.max(0, Math.round(battery.position.y / METRES_PER_UNIT));
  return (Math.floor(altitudeUnits / 2 ** AA_DAT_FIELDS.fuseAltitudeShift) + FUSE_BASE_UNITS) * FLAK_TIMER_UNIT_SECONDS;
}

/** 0, 1 or 2 — the row of the bias and scatter tables this shot is aimed with. */
export function trackingLevel(slot: FlakSlot): number {
  return Math.min(2, Math.max(0, slot.trackingShots));
}

/** True if any battery has a gun laid on this aircraft — what a "ARCHIE SPOTTED" cue keys off. */
export function underFlakFire(batteries: readonly FlakBattery[], aircraftId: string): boolean {
  return batteries.some((battery) => battery.slots.some((slot) => slot.targetId === aircraftId));
}

